import React, { useEffect, useRef, useState } from "react";
import SearchBarComponent from "components/customs/searchbarV2";
import CustomDataTable from "components/customs/table";
import CustomSelect from "components/customs/dropdown";
import { useLocation, useNavigate } from "react-router";
import { defaultTableOption, rowPerPage } from "constants/table";
import { dropdownSelectStyle } from "constants/style";
import { Plus } from "react-feather";
import { getFormattedOptions, showDeleteModal } from "utility/helpers";
import { fetchTable } from "services/services.projects";
import userImage from "assets/img/user.png";
import { TINGOG_URL } from "config";
import { fetchATCList } from "services/services.directory";
import useTextSeeMoreOrLess from "hooks/useTextSeeMoreOrLess";
import {
  ACCOUNT_AGENCY,
  ACCOUNT_HOUSE_SPEAKER,
  CONGRESS_OPTIONS,
} from "utility/constants";
import styled from "styled-components";
import { AddButton } from "assets/js/Styles";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import useAgencies from "hooks/useAgencies";
import useRegions from "hooks/useRegions";
import usePartylists from "hooks/usePartylists";
import useDistricts from "hooks/useDistricts";
import useProvinces from "hooks/useProvinces";
import useMunicipalities from "hooks/useMunicipalities";
import {
  selectorOptions,
  agencyIds,
  HOUSE_SPEAKER_SELECTOR_OPTIONS,
} from "./constants";
import agencyColumns from "./agencyColumns";
import regionalDirectorColumns from "./regionalDirectorColumns";
import congressmanColumns from "./congressmanColumns";
import atcColumns from "./atcColumns";
import nationalAgencyColumns from "./nationalAgencyColumns";
import senatorColumns from "./senatorColumns";
import otherColumns from "./otherColumns";
import mayorColumns from "./mayorColumns";
import governmentColumns from "./governmentColumns";
import { sortArray, sortAtcs } from "./utility";

const TableSection = styled.div`
  margin-top: 16px;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const CardSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const AddButtonText = styled.span`
  padding-left: 8px;
  margin: 0;
  font-weight: 500;
  letter-spacing: 0.63px;
`;

const StyledAddButton = styled(AddButton)`
  width: 130px;
  background: ${({ theme }) => theme.colors.buttonBlue};

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchbarWrapper = styled.div`
  width: 100%;
  position: relative;

  input {
    border: 2px solid ${({ theme }) => theme.colors.black60};
    background: white;
    box-shadow: none;
    border-radius: 9px;
    height: clamp(35px, 5vw, 50px);
  }
`;

const TableHeaderSection = styled.div`
  margin-top: 24px;
  display: grid;
  gap: 16px;
  grid-template-columns: 2fr 1fr 130px;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`;

const SelectContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DirectoryList = () => {
  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [columns, setColumns] = useState([]);
  const [selectedAtcBranch, setSelectedAtcBranch] = useState(null);
  const [atcProgramOptions, setAtcProgramOptions] = useState([]);
  const [selectedAtcProgram, setSelectedAtcProgram] = useState(null);
  const [selectedCongress, setSelectedCongress] = useState([]);
  const skipCongressResetFetchRef = useRef(false);

  const { data: regions } = useRegions();
  const { data: provinces } = useProvinces();
  const { data: municipalities } = useMunicipalities();
  const { data: partyList } = usePartylists();
  const { data: districts } = useDistricts();
  const { acronymOptions: agencyOptions } = useAgencies();

  const location = useLocation();
  const { user } = useAuth();

  const view = location.state?.view || null;
  const agencySelect = location.state?.agencySelect || null;

  const navigate = useNavigate();

  const { insertOrRemoveExpandedSeeMoreOrLessTexts, isTextExpanded } =
    useTextSeeMoreOrLess();

  let changeSearchTimeoutID;

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;

      setTableOption({
        ...tableOption,
        page: 1,
        searchValue: eventValue.trim(),
      });
    }, 1000);
  };

  const onChangePage = (page) => {
    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 10,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column, sortDirection) => {
    if (selectedOption?.value === 2) {
      const newData = sortArray(column, sortDirection, tableOption);

      setTableOption({
        ...tableOption,
        data: newData,
      });

      return;
    }

    if (selectedOption?.value === 4) {
      const newData = sortAtcs(column, sortDirection, tableOption);

      setTableOption({
        ...tableOption,
        data: newData,
      });

      return;
    }

    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  const fetchAtcTable = () => {
    fetchATCList({
      url: `${TINGOG_URL}/integration/directory`,
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        // eslint-disable-next-line camelcase
        search_value: tableOption.searchValue,
        // eslint-disable-next-line camelcase
        atc_branch: selectedAtcBranch?.principal?.atc_name,
        agency: selectedAgency?.id,
        program: selectedAtcProgram?.id,
      },
      callback: (data) => {
        setTableOption({
          ...tableOption,
          isLoading: false,
          data: data.results,
          totalRows: data.count,
        });
      },
    });
  };

  // eslint-disable-next-line consistent-return
  const fetchDataTable = (agency) => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    if (selectedOption.value === 4) return fetchAtcTable();

    let url = "";

    switch (selectedOption.value) {
      case 1:
        url = "/congressman";
        break;

      case 3:
        url = `/regional-director/?agency=${agency || selectedAgency?.value}`;
        break;

      case 5:
        url = "national-agency-user";
        break;

      case 6:
        url = "/official?position=Senator";
        break;

      case 7:
        url = "/official?position=Governor";
        break;

      case 8:
        url = "/official?position=Mayor";
        break;

      case 9:
        url = "/official?position=Vice Governor";
        break;

      case 10:
        url = "/official?position=Vice Mayor";
        break;

      case 11:
        url = "/official?position=Others";
        break;

      default:
        url = "/auth/list-users";
        break;
    }

    fetchTable({
      url,
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        search: tableOption.searchValue,
        column: tableOption.sort.field,
        order: tableOption.sort.direction,
        type: selectedOption.value === 1 ? "page" : null,
        congress:
          selectedOption.value === 1 && selectedCongress.length > 0
            ? selectedCongress.map((c) => c.value).join(",")
            : undefined,
      },
      callback: (data) => {
        let rows = [];
        let totalRows = 0;

        switch (selectedOption.value) {
          case 2:
            rows = data.data.filter((__data) => __data.type === ACCOUNT_AGENCY);
            totalRows = rows.length;
            break;

          default:
            rows = data.rows;
            totalRows = data.count;
            break;
        }

        setTableOption({
          ...tableOption,
          data: rows,
          totalRows,
          isLoading: false,
        });
      },
    });
  };

  const fetchAgencyPrograms = () => {
    fetchTable({
      url: `/agency/${selectedAgency.id}`,
      params: {},
      callback: (data) => {
        const { Projects } = data;

        if (Projects?.length) {
          const options = getFormattedOptions("name", Projects);

          if (options) {
            setAtcProgramOptions(options);
            setSelectedAtcProgram(options[0]);
          }
        } else {
          setAtcProgramOptions([]);
          setSelectedAtcProgram(null);
        }
      },
    });
  };

  const handleDeleteAction = (row) => {
    let url = "";
    const { value } = selectedOption;

    switch (value) {
      case 3:
        url = `/regional-director/${row.id}`;
        break;

      case 2:
        url = `/auth/delete-user/${row.username}`;
        break;

      case 1:
        url = `/congressman/${row.id}`;
        break;

      case 5:
        url = `/national-agency-user/${row.id}`;
        break;
      default:
        url = `/official/${row.id}`;
        break;
    }

    showDeleteModal({
      title: `${row?.firstName} ${row?.lastName}`,
      url,
      callBack: () => {
        fetchDataTable(selectedAgency?.value);
      },
    });
  };

  const AGENCY_COLUMNS = agencyColumns({
    userImage,
    navigate,
    handleDeleteAction,
  });

  const REGIONAL_DIRECTOR_COLUMNS = regionalDirectorColumns({
    navigate,
    handleDeleteAction,
  });

  const CONGRESSMAN_COLUMNS = congressmanColumns({
    districts,
    partyList,
    navigate,
    handleDeleteAction,
  });

  const ATC_COLUMNS = atcColumns({
    isTextExpanded,
    insertOrRemoveExpandedSeeMoreOrLessTexts,
  });

  const NATIONAL_AGENCY_COLUMNS = nationalAgencyColumns({
    userImage,
    navigate,
    handleDeleteAction,
  });

  const SENATOR_COLUMNS = senatorColumns({
    userImage,
    partyList,
    navigate,
    handleDeleteAction,
  });

  const OTHER_COLUMNS = otherColumns({
    userImage,
    navigate,
    handleDeleteAction,
  });

  const MAYOR_COLUMNS = mayorColumns({
    userImage,
    regions,
    provinces,
    municipalities,
    partyList,
    selectedOption,
    navigate,
    handleDeleteAction,
  });

  const GOVERNOR_COLUMNS = governmentColumns({
    userImage,
    navigate,
    regions,
    provinces,
    partyList,
    selectedOption,
    handleDeleteAction,
  });

  useEffect(() => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    if (view) {
      const option = selectorOptions.find((options) =>
        options.label
          .toLocaleLowerCase()
          .includes(view.toLocaleLowerCase() || "")
      );

      setSelectedOption(option);
    } else {
      setSelectedOption(selectorOptions[0]);
    }
  }, []);

  useEffect(() => {
    if (selectedOption !== null && partyList?.length && districts?.length) {
      if (selectedOption.value === 1) {
        setColumns(CONGRESSMAN_COLUMNS);
      }

      if (selectedOption.value === 2) {
        setColumns(AGENCY_COLUMNS);
      } else if (selectedOption.value === 3) {
        setColumns(REGIONAL_DIRECTOR_COLUMNS);

        if (agencyOptions?.length) {
          if (agencySelect) {
            setSelectedAgency(
              agencyOptions.find((_agency) => _agency.id === agencySelect) ??
                agencyOptions[0]
            );
          } else {
            setSelectedAgency(agencyOptions[0]);
          }
        }
      } else if (selectedOption.value === 4) {
        // set default selected agency to DOH
        if (agencyOptions?.length) {
          const option = agencyOptions.find((agency) => agency.id === 8);

          setSelectedAgency(option);
        }
        setSelectedAtcBranch(null);
        setColumns(ATC_COLUMNS);
      } else if (selectedOption.value === 5) {
        setColumns(NATIONAL_AGENCY_COLUMNS);
      } else if (selectedOption.value === 6) {
        setColumns(SENATOR_COLUMNS);
      } else if (selectedOption.value === 7) {
        setColumns(GOVERNOR_COLUMNS);
      } else if (selectedOption.value === 8) {
        setColumns(MAYOR_COLUMNS);
      } else if (selectedOption.value === 9) {
        setColumns(GOVERNOR_COLUMNS);
      } else if (selectedOption.value === 10) {
        setColumns(MAYOR_COLUMNS);
      } else if (selectedOption.value === 11) {
        setColumns(OTHER_COLUMNS);
      }

      fetchDataTable();
    }
  }, [selectedOption, districts, partyList, regions, agencyOptions]);

  useEffect(() => {
    if (
      selectedOption !== null &&
      selectedOption.value !== 1 &&
      selectedCongress.length > 0
    ) {
      skipCongressResetFetchRef.current = true;
      setSelectedCongress([]);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (selectedOption) {
      if (skipCongressResetFetchRef.current) {
        skipCongressResetFetchRef.current = false;

        return;
      }

      fetchDataTable();
    }
  }, [
    selectedAgency,
    selectedCongress,
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.field,
    tableOption.sort.direction,
  ]);

  useEffect(() => {
    if (selectedAgency) {
      fetchAgencyPrograms();
    }
  }, [selectedAgency]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Wrapper>
      <CardSection>
        <CardContainer>
          <TableHeaderSection>
            <SelectContainer>
              <CustomSelect
                isLoading={tableOption.isLoading}
                isDisabled={tableOption.isLoading}
                onChange={(option) => setSelectedOption(option)}
                value={selectedOption}
                options={
                  user?.type === ACCOUNT_HOUSE_SPEAKER
                    ? HOUSE_SPEAKER_SELECTOR_OPTIONS
                    : selectorOptions
                }
                styles={dropdownSelectStyle}
              />

              {selectedOption?.value === 1 && (
                <CustomSelect
                  isMulti
                  isLoading={tableOption.isLoading}
                  isDisabled={tableOption.isLoading}
                  onChange={(options) => {
                    setSelectedCongress(options || []);

                    setTableOption({
                      ...tableOption,
                      page: 1,
                      offset: 0,
                    });
                  }}
                  placeholder="Congress"
                  value={selectedCongress}
                  options={CONGRESS_OPTIONS}
                  styles={dropdownSelectStyle}
                />
              )}

              {(selectedOption?.value === 3 || selectedOption?.value === 4) && (
                <CustomSelect
                  isLoading={tableOption.isLoading}
                  isDisabled={tableOption.isLoading}
                  onChange={(option) => setSelectedAgency(option)}
                  placeholder="Agency"
                  value={selectedAgency}
                  options={
                    selectedOption?.value === 4
                      ? agencyOptions.filter((agency) =>
                          agencyIds.includes(agency.id)
                        )
                      : agencyOptions
                  }
                  styles={dropdownSelectStyle}
                />
              )}

              {selectedOption?.value === 4 && (
                <CustomSelect
                  isLoading={tableOption.isLoading}
                  isDisabled={tableOption.isLoading || !selectedAgency}
                  onChange={(option) => setSelectedAtcProgram(option)}
                  placeholder="Program"
                  value={selectedAtcProgram}
                  options={atcProgramOptions}
                  styles={dropdownSelectStyle}
                />
              )}
            </SelectContainer>

            {/* searchbar here */}
            <SearchbarWrapper>
              <SearchBarComponent
                onChangeSearch={onChangeSearch}
                isDisabled={tableOption?.isLoading}
              />
            </SearchbarWrapper>

            {selectedOption?.value !== 4 && (
              <StyledAddButton
                disabled={tableOption.isLoading}
                onClick={() => {
                  if (selectedOption.value === 1) {
                    navigate("/cong");
                  } else if (selectedOption.value === 2) {
                    navigate("/agency");
                  } else if (selectedOption.value === 3) {
                    navigate("/director", {
                      state: {
                        agency: selectedAgency.value,
                      },
                    });
                  } else if (selectedOption.value === 5) {
                    navigate("/national");
                  } else if (selectedOption.value === 6) {
                    navigate("/sen");
                  } else if (selectedOption.value === 7) {
                    navigate("/gov");
                  } else if (selectedOption.value === 8) {
                    navigate("/mayor");
                  } else if (selectedOption.value === 9) {
                    navigate("/vicegov");
                  } else if (selectedOption.value === 10) {
                    navigate("/vicemayor");
                  } else if (selectedOption.value === 11) {
                    navigate("/others");
                  }
                }}
              >
                <Plus size={24} />
                <AddButtonText>{"Add"}</AddButtonText>
              </StyledAddButton>
            )}
          </TableHeaderSection>

          <TableSection>
            <CustomDataTable
              columns={columns}
              data={tableOption.data}
              noHeader
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onSort={onSort}
              paginationPerPage={tableOption.currentRowsPerPage}
              paginationServer
              paginationTotalRows={tableOption.totalRows}
              progressPending={tableOption.isLoading}
              sortServer
              height="calc(100vh - 200px)"
            />
          </TableSection>
        </CardContainer>
      </CardSection>
    </Wrapper>
  );
};

export default DirectoryList;
